import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/common/PageHeader";
import { CdtStandard } from "@/components/common/CdtStandard";
import { AppointmentCTA } from "@/components/sections/AppointmentCTA";
import { Reveal } from "@/components/common/Reveal";
import { CheckCircle2, Clock, Users, ArrowRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setService(null); setNotFound(false);
    api.get(`/services/${slug}`).then((r) => setService(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-6 py-32 text-center">
      <h1 className="font-display text-3xl">Service not found</h1>
      <Link to="/services" className="mt-4 inline-block text-[var(--cdt-primary)] font-semibold">Back to services</Link>
    </div>
  );

  if (!service) return <div className="max-w-6xl mx-auto px-6 py-24 space-y-6"><Skeleton className="h-12 w-2/3" /><Skeleton className="h-64 w-full rounded-[var(--cdt-radius-lg)]" /></div>;

  const jsonLd = { "@context": "https://schema.org", "@type": "MedicalProcedure", name: service.name, description: service.summary };

  return (
    <>
      <Seo title={service.name} description={service.summary} path={`/services/${slug}`} image={service.cover_image} jsonLd={jsonLd} />
      <PageHeader eyebrow={service.category} title={service.name} subtitle={service.summary} image={service.cover_image} />

      <section className="py-14 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_320px] gap-10">
          <div>
            <Reveal>
              <h2 className="font-display text-2xl md:text-3xl text-[var(--cdt-text)]">About this treatment</h2>
              <p className="mt-4 text-[var(--cdt-muted)] leading-8">{service.overview}</p>
            </Reveal>
            <Reveal>
              <h3 className="font-display text-xl mt-10 text-[var(--cdt-text)]">Options we may recommend</h3>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {service.options?.map((o) => (
                  <div key={o} className="flex items-center gap-2.5 rounded-[var(--cdt-radius-sm)] bg-white border border-[color:var(--cdt-border)] px-4 py-3">
                    <CheckCircle2 className="h-5 w-5 text-[var(--cdt-primary)] flex-shrink-0" />
                    <span className="text-sm text-[var(--cdt-text)]">{o}</span>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal>
              <div className="mt-12 rounded-[var(--cdt-radius-md)] bg-[var(--cdt-surface-2)] border border-[color:var(--cdt-border)] p-6">
                <h3 className="font-display text-xl text-[var(--cdt-text)]">How CDT approaches this</h3>
                <p className="mt-2 text-sm text-[var(--cdt-muted)] leading-7">We follow our five-step standard — so you're always heard, informed, and treated with precision.</p>
                <div className="mt-6"><CdtStandard /></div>
              </div>
            </Reveal>
          </div>

          <aside className="lg:sticky lg:top-24 h-fit space-y-4">
            <div className="rounded-[var(--cdt-radius-md)] bg-white border border-[color:var(--cdt-border)] shadow-[var(--cdt-shadow-sm)] p-6">
              <div className="flex items-start gap-3"><Users className="h-5 w-5 text-[var(--cdt-primary)] mt-0.5" /><div><p className="text-xs uppercase tracking-wide text-[var(--cdt-muted)]">Who it's for</p><p className="text-sm text-[var(--cdt-text)] mt-1">{service.who_for}</p></div></div>
              <div className="flex items-start gap-3 mt-4"><Clock className="h-5 w-5 text-[var(--cdt-primary)] mt-0.5" /><div><p className="text-xs uppercase tracking-wide text-[var(--cdt-muted)]">Typical timeline</p><p className="text-sm text-[var(--cdt-text)] mt-1">{service.timeline}</p></div></div>
              <Link to="/appointment" data-testid="service-appointment-button" className="mt-6 w-full inline-flex items-center justify-center gap-2 h-11 rounded-[var(--cdt-radius-sm)] bg-[var(--cdt-primary)] text-white font-semibold hover:bg-[var(--cdt-primary-2)] transition-colors">Request Appointment <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </aside>
        </div>
      </section>
      <AppointmentCTA />
    </>
  );
}
